import { TranslateModule } from "@ngx-translate/core";
import { Component, ViewEncapsulation, computed, input, output } from "@angular/core";

/**
 * @description
 * What a screen shows in place of data it could not get.
 *
 * The failure being designed out is a control that renders empty and looks like a real answer. An
 * empty nationality dropdown tells a clerk nothing about *why* it is empty, so they retry the whole
 * booking, or worse, tell the visitor the museum has no tickets. This component exists so
 * "unavailable" never looks like "none".
 *
 * Deliberately separate from an empty state rather than an empty state with a different message —
 * T-12 asks for the two to be visually distinct, because the clerk's next action differs: an empty
 * list means move on, an unavailable one means wait or reconnect.
 *
 * `blocking` is the honest-failure marker. A step that cannot be filled safely without this data
 * (dates, prices, required fields) says so; an optional one (extras, products) says the visit can
 * continue without it. Getting that split wrong in either direction is the main risk in T-12, so it
 * is stated at every call site rather than inferred here.
 */
@Component({
  selector: "app-offline-state",
  standalone: true,
  imports: [TranslateModule],
  templateUrl: "./offline-state.component.html",
  styleUrl: "./offline-state.component.scss",
  encapsulation: ViewEncapsulation.None,
})
export class OfflineStateComponent {
  /** `unavailable` — could not reach the server. `failed` — the server answered with an error. */
  readonly status = input.required<"unavailable" | "failed">();

  /** True when the step cannot be completed without this data, so the message must say so. */
  readonly blocking = input(false);

  /** Overrides the body text with a call-site-specific key — e.g. "prices could not be loaded". */
  readonly messageKey = input<string>();

  /** Hidden where nothing sensible can be retried. */
  readonly canRetry = input(true);

  /** Inline sizing for a state that sits inside a form field rather than filling a page. */
  readonly compact = input(false);

  readonly retry = output<void>();

  readonly title = computed(() => (this.status() === "unavailable" ? "OFFLINE_STATE.UNAVAILABLE" : "OFFLINE_STATE.FAILED"));

  readonly body = computed(() => {
    const override = this.messageKey();
    if (override) return override;

    if (this.status() === "failed") return "OFFLINE_STATE.FAILED_HINT";

    return this.blocking() ? "OFFLINE_STATE.UNAVAILABLE_BLOCKING_HINT" : "OFFLINE_STATE.UNAVAILABLE_OPTIONAL_HINT";
  });
}
